import { useState, useRef, useEffect } from "react"
import { Play, Pause, Loader2, RefreshCcw } from "lucide-react"
import { useProductTourV2 } from "../stores/use-product-tour"
import { useCosmos } from "@/modules/cosmos/stores/use-cosmos-store"
import { useAuth } from "@/modules/auth/store"
import { PRODUCT_TOUR_V2_STEPS } from "../types/steps"
import mixpanelClient from "@/services/mixpanel"
import useUpdateProductTourStatus from "../queries/use-update-status"

interface CircularVideoPlayerProps {
  videoUrl: string
  size?: number
  showControls?: boolean
}

export const CircularVideoPlayer = ({
  videoUrl,
  size = 280,
  showControls = true,
}: CircularVideoPlayerProps) => {
  const [currentVideoUrl, setCurrentVideoUrl] = useState(videoUrl)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [isHovered, setIsHovered] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const animationRef = useRef<number>()
  const { currentStep } = useProductTourV2()
  const { cosmosKey } = useCosmos()
  const { user } = useAuth()
  const { mutate: updateProductTourStatus } = useUpdateProductTourStatus()
  // Draw circular video on canvas
  const drawCircularVideo = () => {
    const video = videoRef.current
    const canvas = canvasRef.current

    if (!video || !canvas || video.readyState < 2) return

    const ctx = canvas.getContext("2d")
    if (!ctx) return

    const centerX = canvas.width / 2
    const centerY = canvas.height / 2
    const radius = Math.min(centerX, centerY)

    // Clean canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    // Create circular mask
    ctx.save()
    ctx.beginPath()
    ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI)
    ctx.clip()

    // Calculate dimensions to maintain aspect ratio
    const videoAspect = video.videoWidth / video.videoHeight
    const canvasAspect = canvas.width / canvas.height

    let drawWidth, drawHeight, drawX, drawY

    if (videoAspect > canvasAspect) {
      // Video is wider, adjust by height
      drawHeight = canvas.height
      drawWidth = drawHeight * videoAspect
      drawX = (canvas.width - drawWidth) / 2
      drawY = 0
    } else {
      // Video is taller, adjust by width
      drawWidth = canvas.width
      drawHeight = drawWidth / videoAspect
      drawX = 0
      drawY = (canvas.height - drawHeight) / 2
    }

    // Draw video
    ctx.drawImage(video, drawX, drawY, drawWidth, drawHeight)
    ctx.restore()

    // Continue animation only if playing
    if (!video.paused && !video.ended) {
      animationRef.current = requestAnimationFrame(drawCircularVideo)
    }
  }

  // Function to draw a single frame (when paused)
  const drawSingleFrame = () => {
    const video = videoRef.current
    const canvas = canvasRef.current

    if (!video || !canvas || video.readyState < 2) return

    const ctx = canvas.getContext("2d")
    if (!ctx) return

    const centerX = canvas.width / 2
    const centerY = canvas.height / 2
    const radius = Math.min(centerX, centerY)

    // Clean canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    // Create circular mask
    ctx.save()
    ctx.beginPath()
    ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI)
    ctx.clip()

    // Calculate dimensions to maintain aspect ratio
    const videoAspect = video.videoWidth / video.videoHeight
    const canvasAspect = canvas.width / canvas.height

    let drawWidth, drawHeight, drawX, drawY

    if (videoAspect > canvasAspect) {
      // Video is wider, adjust by height
      drawHeight = canvas.height
      drawWidth = drawHeight * videoAspect
      drawX = (canvas.width - drawWidth) / 2
      drawY = 0
    } else {
      // Video is taller, adjust by width
      drawWidth = canvas.width
      drawHeight = drawWidth / videoAspect
      drawX = 0
      drawY = (canvas.height - drawHeight) / 2
    }

    // Draw video
    ctx.drawImage(video, drawX, drawY, drawWidth, drawHeight)
    ctx.restore()
  }

  useEffect(() => {
    if (currentStep === PRODUCT_TOUR_V2_STEPS.STUDY && isPlaying) {
      mixpanelClient.productTour.startFirstVideo({
        email: user.emailAddress,
        name: `${user.firstName} ${user.lastName}`,
        organization: cosmosKey,
      })
    }
    if (currentStep === PRODUCT_TOUR_V2_STEPS.MY_SITUATION && isPlaying) {
      mixpanelClient.productTour.startSecondVideo({
        email: user.emailAddress,
        name: `${user.firstName} ${user.lastName}`,
        organization: cosmosKey,
      })
    }
  }, [currentStep, isPlaying])

  // Effect to handle navigation away from my-situation page
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (location.pathname === "/my-situation") {
        updateProductTourStatus("completed")
      }
    }

    const handlePopState = () => {
      if (location.pathname === "/my-situation") {
        updateProductTourStatus("completed")
      }
    }

    // Handle browser navigation (back/forward buttons, refresh, close)
    window.addEventListener("beforeunload", handleBeforeUnload)
    window.addEventListener("popstate", handlePopState)

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload)
      window.removeEventListener("popstate", handlePopState)
      if (location.pathname === "/my-situation") {
        updateProductTourStatus("completed")
      }
    }
  }, [location.pathname])

  // Effect to change video dynamically
  useEffect(() => {
    setCurrentVideoUrl(videoUrl)
    setIsLoading(true)
    setCurrentTime(0)
    setDuration(0)
    setIsPlaying(false)
  }, [videoUrl])

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const handleLoadedMetadata = () => {
      setDuration(video.duration)
      setIsLoading(false)
      // Ensure the video is on the first frame
      video.currentTime = 0.1
    }

    const handleLoadedData = () => {
      setIsLoading(false)
      drawSingleFrame() // Draw the first frame when the data is loaded
    }

    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime)
      // Draw frame when the time changes (for seeking)
      if (video.paused) {
        drawSingleFrame()
      }
    }

    const handlePlay = () => {
      setIsPlaying(true)
      setIsLoading(false)
      drawCircularVideo()
    }

    const handlePause = () => {
      setIsPlaying(false)
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
      // Draw the current frame when paused
      drawSingleFrame()
    }

    const handleLoadStart = () => {
      setIsLoading(true)
    }

    const handleCanPlay = () => {
      setIsLoading(false)
      // Draw frame when it can be played
      setTimeout(() => drawSingleFrame(), 100)
    }

    const handleSeeked = () => {
      // Draw frame after seeking
      drawSingleFrame()
    }

    const handleError = () => {
      setIsLoading(false)
      console.error("Error loading video:", currentVideoUrl)
    }

    const handleEnded = () => {
      setIsPlaying(false)
      setCurrentTime(0)
      video.currentTime = 0
      // Draw the first frame when video ends
      drawSingleFrame()
    }

    video.addEventListener("loadedmetadata", handleLoadedMetadata)
    video.addEventListener("loadeddata", handleLoadedData)
    video.addEventListener("timeupdate", handleTimeUpdate)
    video.addEventListener("play", handlePlay)
    video.addEventListener("pause", handlePause)
    video.addEventListener("loadstart", handleLoadStart)
    video.addEventListener("canplay", handleCanPlay)
    video.addEventListener("seeked", handleSeeked)
    video.addEventListener("error", handleError)
    video.addEventListener("ended", handleEnded)

    return () => {
      video.removeEventListener("loadedmetadata", handleLoadedMetadata)
      video.removeEventListener("loadeddata", handleLoadedData)
      video.removeEventListener("timeupdate", handleTimeUpdate)
      video.removeEventListener("play", handlePlay)
      video.removeEventListener("pause", handlePause)
      video.removeEventListener("loadstart", handleLoadStart)
      video.removeEventListener("canplay", handleCanPlay)
      video.removeEventListener("seeked", handleSeeked)
      video.removeEventListener("error", handleError)
      video.removeEventListener("ended", handleEnded)
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [currentVideoUrl])

  const togglePlayPause = () => {
    const video = videoRef.current
    if (!video || isLoading) return

    // If video has ended or is at the end, restart from beginning
    if (video.ended || video.currentTime >= video.duration) {
      video.currentTime = 0
      setCurrentTime(0)
    }

    if (isPlaying) {
      video.pause()
      setIsPlaying(false)
    } else {
      video.play().catch(console.error)
      setIsPlaying(true)
    }
  }

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60)
    const seconds = Math.floor(time % 60)
    return `${minutes}:${seconds.toString().padStart(2, "0")}`
  }
  return (
    <div
      className="relative inline-block"
      style={{ width: `${size}px`, height: `${size}px` }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Hidden video for processing */}
      <video
        ref={videoRef}
        src={currentVideoUrl}
        className="pointer-events-none absolute opacity-0"
        autoPlay={false}
        loop={false}
        muted={false}
        playsInline
        crossOrigin="anonymous"
      />

      {/* Canvas circular */}
      <canvas
        ref={canvasRef}
        width={size}
        height={size}
        className="cursor-pointer rounded-full border-4 border-white shadow-lg dark:border-gray-800"
        onClick={togglePlayPause}
        style={{ backgroundColor: "#f3f4f6" }} // Fallback color while loading
      />

      {/* Loading indicator */}
      {isLoading && (
        <div className="absolute -inset-2.5 flex items-center justify-center rounded-full bg-white">
          <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
        </div>
      )}

      {/* Overlay controls */}
      {showControls && !isLoading && (
        <div
          className={`absolute inset-0 transition-opacity duration-300 ${isHovered ? "opacity-100" : "opacity-0"}`}
        >
          {/* Central play/pause button */}
          <div className="absolute inset-0 flex items-center justify-center">
            <button
              onClick={(e) => {
                e.stopPropagation()
                togglePlayPause()
              }}
              className="z-50 rounded-full bg-black/50 p-4 text-white transition-colors hover:bg-black/70"
              disabled={isLoading}
            >
              {isPlaying ? <Pause size={24} /> : <Play size={24} />}
            </button>
          </div>

          {/* Restart button */}
          <button
            onClick={(e) => {
              e.stopPropagation()
              if (videoRef.current) {
                videoRef.current.pause()
                videoRef.current.currentTime = 0
              }
            }}
            className="absolute right-2 top-2 z-50 rounded-full bg-black/50 p-2 text-white transition-colors hover:bg-black/70"
            disabled={isLoading}
          >
            <RefreshCcw size={16} />
          </button>

          {/* Circular progress bar */}
          <div className="absolute bottom-0 left-0 right-0 top-0 ml-[0.22rem] mt-[0.22rem]">
            <svg width={size} height={size} className="-rotate-90 transform">
              <circle
                cx={size / 2}
                cy={size / 2}
                r={size / 2 - 10}
                fill="none"
                stroke="rgba(255,255,255,0.3)"
                strokeWidth="4"
              />
              <circle
                cx={size / 2}
                cy={size / 2}
                r={size / 2 - 10}
                fill="none"
                strokeWidth="4"
                strokeDasharray={`${2 * Math.PI * (size / 2 - 10)}`}
                strokeDashoffset={`${2 * Math.PI * (size / 2 - 10) * (1 - (duration > 0 ? currentTime / duration : 0))}`}
                className="stroke-primary-500 transition-all duration-200"
              />
            </svg>
          </div>

          {/* Time */}
          <div className="absolute bottom-2 left-2 rounded bg-black/50 px-2 py-1 text-xs text-white">
            {formatTime(currentTime)} / {formatTime(duration)}
          </div>
        </div>
      )}
    </div>
  )
}
